import React, { useState, useEffect, useRef } from "react";
import { Brain, Send, ChevronRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import DynamicChart from "../chatbotConfig/DynamicChart";

const DashboardChatbot = ({
  messages = [],
  followUpOptions = null,
  isLoading = false,
  onSendMessage,
}) => {
  const [input, setInput] = useState("");
  const chatContainerRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const sampleQuestions = [
    {
      id: 1,
      text: "I notice some potential risks in Generator Alpha's performance metrics. Would you like me to analyze and show you a trend graph?",
      type: "risk_alert",
    },
    {
      id: 2,
      text: "The cooling system efficiency has dropped by 7% this week. Should I show you the temperature patterns?",
      type: "efficiency_alert",
    },
    {
      id: 3,
      text: "There's an unusual pattern in energy consumption across night shifts. Would you like to see the comparison?",
      type: "pattern_alert",
    },
  ]; // Auto-scroll when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);
  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    onSendMessage(input);
    setInput("");
  };

  const handleFollowUpClick = (option) => {
    onSendMessage(option);
  };

  const renderMessageContent = (msg) => {
    return (
      <div className="space-y-4">
        <div className="prose prose-invert">
          <ReactMarkdown>{msg.content}</ReactMarkdown>
        </div>
        {/* {msg.graph && (
          <div className="mt-4 h-64 w-full">
            <DynamicChart data={msg.graph} />
          </div>
        )} */}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl border border-gray-700">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b border-gray-700">
        <img
          style={{
            borderRadius: "100%",
            border: "none",
            fontSize: "14px",
            cursor: "pointer",
            height: "45px",
            width: "45px",
          }}
          src="ai_icon.svg"
          className=" top-0 h-12 w-12 cursor-pointer"
          alt="Chat Icon"
        />
        <div>
          <h2 className="text-lg font-semibold text-white">Apollo AI Agent</h2>
          <p className="text-sm text-gray-400">
            Real-time monitoring & insights
          </p>
        </div>
      </div>
      {/* Messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${
              msg.type === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-lg ${
                msg.type === "user"
                  ? "bg-orange-500/20 text-white"
                  : msg.alertType
                  ? "bg-red-500/10 text-white border border-red-500/20"
                  : "bg-gray-800 text-white"
              }`}
            >
              {renderMessageContent(msg)}
              {msg.alertType && (
                <button
                  onClick={() => handleFollowUpClick("Show me the analysis")}
                  className="mt-2 text-sm text-orange-500 hover:text-orange-400 flex items-center gap-1"
                >
                  View Analysis
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-800 text-white p-3 rounded-lg max-w-[80%] flex items-center gap-2">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.4s" }}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>{" "}
      {/* Follow-up Questions */}
      {followUpOptions && followUpOptions.options.length > 0 && (
        <div className="p-4 border-t border-gray-700 bg-gray-800/50">
          <p className="text-sm text-gray-400 mb-2">
            {followUpOptions.question || "Suggested follow-up questions:"}
          </p>
          <div className="flex flex-wrap gap-2">
            {followUpOptions.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleFollowUpClick(option)}
                className="text-sm px-3 py-1.5 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors flex items-center gap-1"
              >
                <span>{option}</span>
                <ChevronRight className="h-3 w-3 text-gray-400" />
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-gray-700">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about asset performance, risks, or trends..."
            className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <button
            type="submit"
            disabled={isLoading}
            className={`p-2 bg-orange-500 rounded-lg transition-colors ${
              isLoading
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-orange-600"
            }`}
          >
            <Send className="h-5 w-5 text-white" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default DashboardChatbot;
